import { useSnackbar } from 'notistack'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { useSendExpertiseMutation } from '@/core/services/expertise'
import { getApiErrorMessage } from '@/utils/errors'

const schema = z.object({
  networkId: z.string().min(1),
})

export type SelectNetworkFormData = z.infer<typeof schema>

export function useSelectNetworkForm(expertiseId: string) {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [sendExpertise, { isLoading: isSendingExpertise }] = useSendExpertiseMutation()

  const rhForm = useForm<SelectNetworkFormData>({
    defaultValues: {
      networkId: '',
    },
    resolver: zodResolver(schema),
  })

  const onSubmit: SubmitHandler<SelectNetworkFormData> = async (data) => {
    try {
      const expertise = await sendExpertise({ id: expertiseId, receiverId: data.networkId, receiverType: 'Network' }).unwrap()
      const network = expertise.network?.name ?? ''
      enqueueSnackbar(t('notification.expertise.send.successToNetwork', { network }), {
        variant: 'success',
      })
      navigate('/expertises')
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.expertise.send.error')), { variant: 'error' })
    }
  }

  return { isSendingExpertise, rhForm, onSubmit }
}
